import axios from "axios";
import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { z } from "zod";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { isQueryReady } from "src/api/axiosClient/utils";
import { productGridKey } from "./useGetAllproduct";
import { getOneProductKey, ProductPhoto, ProductPhotoSchema } from "./useGetOneProduct";

export const getProductPhotosKey = "getProductPhotos" as const;
const createProductPhotoUploadUrlKey = "createProductPhotoUploadUrl" as const;

const productPhotoUploadUrlSchema = z.object({
    presignedUrl: z.string(),
    objectKey: z.string(),
    url: z.string(),
});

type ProductPhotoUploadUrl = z.infer<typeof productPhotoUploadUrlSchema>;

type UploadProductPhotoOptions = {
    productId: number;
    file: File;
    isPrimary?: boolean;
    alt?: string | null;
    replacePhotos?: ProductPhoto[];
};

type CreateProductPhotoOptions = {
    productId: number;
    photo: {
        objectKey: string;
        url: string;
        originalName: string;
        mimeType: string;
        size: number;
        isPrimary?: boolean;
        alt?: string | null;
    };
};

type ProductPhotoUploadAttempt = {
    uploadUrl?: ProductPhotoUploadUrl;
    uploadUrlPromise?: Promise<ProductPhotoUploadUrl>;
    binaryUploaded: boolean;
    binaryUploadPromise?: Promise<void>;
    metadataAttempted: boolean;
    metadataError?: unknown;
    uploadedPhoto?: ProductPhoto;
    uploadedPhotoPromise?: Promise<ProductPhoto>;
};

const PRODUCT_PHOTO_PAGE_SIZE = 100;
const MAX_PRODUCT_PHOTO_PAGES = 1_000;

const ProductPhotoCollectionSchema = z
    .object({
        "hydra:member": ProductPhotoSchema.array().optional(),
        "hydra:totalItems": z.number().int().nonnegative().optional(),
        "hydra:view": z
            .object({
                "hydra:next": z.string().min(1).optional(),
            })
            .optional(),
        member: ProductPhotoSchema.array().optional(),
        totalItems: z.number().int().nonnegative().optional(),
    })
    .refine((collection) => collection["hydra:member"] !== undefined || collection.member !== undefined, {
        message: "Missing product photo member array",
    });

const parseProductPhotoCollection = (data: unknown) => {
    const collection = zodParseWithLog(data, ProductPhotoCollectionSchema, getProductPhotosKey);
    const members = collection["hydra:member"] ?? collection.member ?? [];
    const totalItems = collection["hydra:totalItems"] ?? collection.totalItems;
    if (
        collection["hydra:totalItems"] !== undefined &&
        collection.totalItems !== undefined &&
        collection["hydra:totalItems"] !== collection.totalItems
    ) {
        throw new Error("Conflicting product photo collection totals");
    }
    const next = collection["hydra:view"]?.["hydra:next"];

    return { members, totalItems, next };
};

const normalizeProductPhotoNextLink = (nextLink: string, endpoint: string, expectedPage: number) => {
    const fallbackBaseUrl = "https://product-photos.local";
    let endpointUrl: URL;
    let nextUrl: URL;
    try {
        const apiBaseUrl = new URL(loggedInApiCall.defaults.baseURL ?? "/", fallbackBaseUrl);
        endpointUrl = new URL(endpoint, apiBaseUrl);
        nextUrl = new URL(nextLink, endpointUrl);
    } catch {
        throw new Error("Invalid product photo collection next link");
    }

    if (nextUrl.origin !== endpointUrl.origin || nextUrl.pathname !== endpointUrl.pathname || nextUrl.hash !== "") {
        throw new Error("Product photo pagination link changes collection scope");
    }

    const allowedParameters = new Set(["itemsPerPage", "page"]);
    const parameters = [...nextUrl.searchParams.keys()];
    if (
        parameters.some((parameter) => !allowedParameters.has(parameter)) ||
        nextUrl.searchParams.getAll("itemsPerPage").length !== 1 ||
        nextUrl.searchParams.get("itemsPerPage") !== PRODUCT_PHOTO_PAGE_SIZE.toString() ||
        nextUrl.searchParams.getAll("page").length !== 1 ||
        nextUrl.searchParams.get("page") !== expectedPage.toString()
    ) {
        throw new Error("Product photo pagination link changes collection parameters");
    }

    return `${nextUrl.pathname}?itemsPerPage=${PRODUCT_PHOTO_PAGE_SIZE}&page=${expectedPage}`;
};

const fetchAllProductPhotos = async (productId: number): Promise<ProductPhoto[]> => {
    const endpoint = `/api/products/${productId}/photos`;
    const photos: ProductPhoto[] = [];
    const photoIds = new Set<string>();
    const visitedLinks = new Set<string>();
    let expectedTotal: number | undefined;
    let nextLink: string | undefined;
    let pagesFetched = 0;

    while (true) {
        if (pagesFetched >= MAX_PRODUCT_PHOTO_PAGES) {
            throw new Error("Product photo pagination exceeded safe page limit");
        }

        const response =
            nextLink ?
                await loggedInApiCall.get(nextLink)
            :   await loggedInApiCall.get(endpoint, {
                    params: { itemsPerPage: PRODUCT_PHOTO_PAGE_SIZE, page: pagesFetched + 1 },
                });
        pagesFetched += 1;
        const collection = parseProductPhotoCollection(response.data);
        if (
            expectedTotal !== undefined &&
            collection.totalItems !== undefined &&
            collection.totalItems !== expectedTotal
        ) {
            throw new Error("Inconsistent product photo collection total");
        }
        expectedTotal ??= collection.totalItems;

        for (const photo of collection.members) {
            if (photoIds.has(photo.id)) {
                throw new Error("Duplicate product photo received while reconciling upload");
            }
            photoIds.add(photo.id);
            photos.push(photo);
        }

        if (expectedTotal !== undefined && photos.length > expectedTotal) {
            throw new Error("Product photo collection exceeds its declared total");
        }
        if (expectedTotal !== undefined && photos.length === expectedTotal) {
            if (collection.next !== undefined) {
                throw new Error("Product photo collection continues beyond its declared total");
            }
            break;
        }

        if (collection.next !== undefined) {
            if (collection.members.length === 0) {
                throw new Error("Product photo pagination made no progress");
            }
            const normalizedNextLink = normalizeProductPhotoNextLink(collection.next, endpoint, pagesFetched + 1);
            if (visitedLinks.has(normalizedNextLink)) {
                throw new Error("Cyclic product photo pagination link");
            }
            visitedLinks.add(normalizedNextLink);
            nextLink = normalizedNextLink;
        } else if (expectedTotal !== undefined && photos.length < expectedTotal) {
            if (collection.members.length === 0) {
                throw new Error("Product photo pagination made no progress");
            }
            nextLink = undefined;
        } else if (collection.members.length === 0) {
            break;
        } else {
            throw new Error("Product photo collection is missing pagination metadata");
        }
    }

    if (expectedTotal !== undefined && photos.length !== expectedTotal) {
        throw new Error("Product photo collection does not match its declared total");
    }

    return photos;
};

const findProductPhotoByObjectKey = async (productId: number, objectKey: string) => {
    const photos = await fetchAllProductPhotos(productId);
    const matches = photos.filter((photo) => photo.objectKey === objectKey);
    if (matches.length > 1) {
        throw new Error("Multiple product photos share the upload object key");
    }

    return matches[0];
};

export const useGetProductPhotos = (productId?: number, enabled = true) => {
    const isEnabled = enabled && productId !== undefined;
    const query = useQuery({
        queryKey: [getProductPhotosKey, productId],
        enabled: isEnabled,
        queryFn: async () => {
            const response = await loggedInApiCall.get(`/api/products/${productId}/photos`);
            return response;
        },
        select: (request) => {
            const data = request.data["hydra:member"] ?? request.data.member ?? [];
            return zodParseWithLog(data, ProductPhotoSchema.array(), getProductPhotosKey);
        },
    });

    return {
        photos: query.data,
        isReady: isQueryReady(query, isEnabled),
        ...query,
    };
};

const createProductPhotoUploadUrl = async (options: UploadProductPhotoOptions): Promise<ProductPhotoUploadUrl> => {
    const { productId, file } = options;
    const response = await loggedInApiCall.post(`/api/products/${productId}/photos/presigned-url`, {
        filename: file.name,
        mimeType: file.type,
        size: file.size,
    });

    return zodParseWithLog(response.data, productPhotoUploadUrlSchema, createProductPhotoUploadUrlKey);
};

const createProductPhoto = async (options: CreateProductPhotoOptions): Promise<ProductPhoto> => {
    const { productId, photo } = options;
    const response = await loggedInApiCall.post(`/api/products/${productId}/photos`, photo);

    return zodParseWithLog(response.data, ProductPhotoSchema, "createProductPhoto");
};

const deleteProductPhoto = async (photo: ProductPhoto): Promise<void> => {
    try {
        await loggedInApiCall.delete(`/api/product-photos/${photo.id}`);
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return;
        }

        throw error;
    }
};

const deleteProductPhotos = async (photos: ProductPhoto[]): Promise<void> => {
    await photos.reduce<Promise<void>>(async (previousDeletion, photo) => {
        await previousDeletion;
        await deleteProductPhoto(photo);
    }, Promise.resolve());
};

export const useUploadProductPhoto = () => {
    const queryClient = useQueryClient();
    const attemptsByFileRef = useRef(new WeakMap<File, Map<number, ProductPhotoUploadAttempt>>());

    const getUploadAttempt = (options: UploadProductPhotoOptions) => {
        let attemptsByProduct = attemptsByFileRef.current.get(options.file);
        if (attemptsByProduct === undefined) {
            attemptsByProduct = new Map();
            attemptsByFileRef.current.set(options.file, attemptsByProduct);
        }

        let attempt = attemptsByProduct.get(options.productId);
        if (attempt === undefined) {
            attempt = {
                binaryUploaded: false,
                metadataAttempted: false,
            };
            attemptsByProduct.set(options.productId, attempt);
        }

        return attempt;
    };

    const ensureUploadUrl = async (attempt: ProductPhotoUploadAttempt, options: UploadProductPhotoOptions) => {
        if (attempt.uploadUrl !== undefined) {
            return attempt.uploadUrl;
        }

        attempt.uploadUrlPromise ??= createProductPhotoUploadUrl(options)
            .then((uploadUrl) => {
                attempt.uploadUrl = uploadUrl;
                return uploadUrl;
            })
            .finally(() => {
                attempt.uploadUrlPromise = undefined;
            });

        return attempt.uploadUrlPromise;
    };

    const ensureBinaryUpload = async (
        attempt: ProductPhotoUploadAttempt,
        uploadUrl: ProductPhotoUploadUrl,
        file: File,
    ) => {
        if (attempt.binaryUploaded) {
            return;
        }

        attempt.binaryUploadPromise ??= axios
            .put(uploadUrl.presignedUrl, file, {
                headers: {
                    "Content-Type": file.type,
                },
            })
            .then(() => {
                attempt.binaryUploaded = true;
            })
            .catch((error: unknown) => {
                const status = axios.isAxiosError(error) ? error.response?.status : undefined;
                if (typeof status === "number" && status >= 400 && status < 500 && attempt.uploadUrl === uploadUrl) {
                    attempt.uploadUrl = undefined;
                }
                throw error;
            })
            .finally(() => {
                attempt.binaryUploadPromise = undefined;
            });

        await attempt.binaryUploadPromise;
    };

    const ensureUploadedPhoto = async (
        attempt: ProductPhotoUploadAttempt,
        options: UploadProductPhotoOptions,
    ): Promise<ProductPhoto> => {
        if (attempt.uploadedPhoto !== undefined) {
            return attempt.uploadedPhoto;
        }
        if (attempt.uploadedPhotoPromise !== undefined) {
            return attempt.uploadedPhotoPromise;
        }

        attempt.uploadedPhotoPromise = (async () => {
            const uploadUrl = await ensureUploadUrl(attempt, options);
            await ensureBinaryUpload(attempt, uploadUrl, options.file);

            if (attempt.metadataAttempted) {
                try {
                    const reconciledPhoto = await findProductPhotoByObjectKey(options.productId, uploadUrl.objectKey);
                    if (reconciledPhoto !== undefined) {
                        attempt.uploadedPhoto = reconciledPhoto;
                        return reconciledPhoto;
                    }
                } catch {
                    throw attempt.metadataError;
                }
            }

            attempt.metadataAttempted = true;
            try {
                const uploadedPhoto = await createProductPhoto({
                    productId: options.productId,
                    photo: {
                        objectKey: uploadUrl.objectKey,
                        url: uploadUrl.url,
                        originalName: options.file.name,
                        mimeType: options.file.type,
                        size: options.file.size,
                        isPrimary: options.isPrimary ?? false,
                        alt: options.alt ?? null,
                    },
                });
                if (uploadedPhoto.objectKey !== uploadUrl.objectKey) {
                    throw new Error("Created product photo does not match the upload object key");
                }
                attempt.uploadedPhoto = uploadedPhoto;
                attempt.metadataError = undefined;
                return uploadedPhoto;
            } catch (error) {
                attempt.metadataError = error;
                try {
                    const reconciledPhoto = await findProductPhotoByObjectKey(options.productId, uploadUrl.objectKey);
                    if (reconciledPhoto !== undefined) {
                        attempt.uploadedPhoto = reconciledPhoto;
                        attempt.metadataError = undefined;
                        return reconciledPhoto;
                    }
                } catch {
                    // Preserve and surface the original metadata error when reconciliation is inconclusive.
                }
                throw error;
            }
        })().finally(() => {
            attempt.uploadedPhotoPromise = undefined;
        });

        return attempt.uploadedPhotoPromise;
    };

    return useMutation({
        mutationFn: async (options: UploadProductPhotoOptions) => {
            const { productId, file, isPrimary = false, alt = null, replacePhotos = [] } = options;
            const attempt = getUploadAttempt({ productId, file, isPrimary, alt, replacePhotos });
            const uploadedPhoto = await ensureUploadedPhoto(attempt, {
                productId,
                file,
                isPrimary,
                alt,
                replacePhotos,
            });

            const photosToDelete = replacePhotos.filter((photo) => photo.id !== uploadedPhoto.id);
            await deleteProductPhotos(photosToDelete);

            return uploadedPhoto;
        },
        onSuccess: (_photo, options) => {
            queryClient.invalidateQueries([getProductPhotosKey, options.productId]);
            queryClient.invalidateQueries([getOneProductKey, options.productId.toString()]);
            queryClient.invalidateQueries([productGridKey]);
        },
    });
};

export const useDeleteProductPhoto = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: { productId: number; photo: ProductPhoto }) => {
            const { photo } = options;
            await deleteProductPhoto(photo);
            return options;
        },
        onSuccess: ({ productId }) => {
            queryClient.invalidateQueries([productGridKey]);
            queryClient.invalidateQueries([getProductPhotosKey, productId]);
            queryClient.invalidateQueries([getOneProductKey, productId.toString()]);
        },
    });
};
